import {sanitizeOnboardingAnswers} from '../../supabase/functions/_shared/onboardingAnswers';
import type {OnboardingAnswers} from './onboardingConfig';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type SavedPlan = {version: 1; savedAt: number; checkout: 'guest' | 'account'; answers: OnboardingAnswers};
const lifetime = 30 * 86400000;
const storageKey = (search: string) => {
  const query = new URLSearchParams(search), code = query.get('qz');
  return `focuscorp_saved_quiz_plan_v1:${code === 'fc1' || code === 'tst1' ? code : 'organic'}:${query.get('flow') === 'account' ? 'account' : 'default'}`;
};
function cleanAnswers(input: unknown): OnboardingAnswers {
  const answers: OnboardingAnswers = sanitizeOnboardingAnswers(input);
  if (input && typeof input === 'object' && 'entryIdentity' in input
    && (input.entryIdentity === 'founder' || input.entryIdentity === 'scroller')) answers.entryIdentity = input.entryIdentity;
  return answers;
}
export function saveQuizPlan(answers: unknown, checkout: SavedPlan['checkout'], search: string, storage?: Store, now = Date.now()) {
  try { (storage ?? localStorage).setItem(storageKey(search), JSON.stringify({version: 1, savedAt: now, checkout, answers: cleanAnswers(answers)})); }
  catch { /* Browser storage can be unavailable; leaving must still work. */ }
}
export function readQuizPlan(search: string, storage?: Store, now = Date.now()): SavedPlan | null {
  try {
    const store = storage ?? localStorage, key = storageKey(search);
    const saved = JSON.parse(store.getItem(key) || 'null');
    if (!saved || saved.version !== 1 || !['guest', 'account'].includes(saved.checkout) || !Number.isFinite(saved.savedAt)) return null;
    if (now < saved.savedAt || now - saved.savedAt >= lifetime) { store.removeItem(key); return null; }
    const answers = cleanAnswers(saved.answers);
    return Object.keys(answers).length ? {...saved, answers} : null;
  } catch { return null; }
}
export function clearQuizPlan(search: string, storage?: Store) {
  try { (storage ?? localStorage).removeItem(storageKey(search)); } catch { /* Optional browser persistence. */ }
}

/** A deliberate new quiz gets a new draft; paid purchase recovery and Auth stay intact. */
export function resetGuestQuizDraft(backendUrl: string | undefined, storage: Pick<Storage, 'removeItem'>) {
  try {
    storage.removeItem('focuscorp_guest_onboarding_draft');
    storage.removeItem('focuscorp_checkout_refresh_plan_review');
    if (backendUrl) storage.removeItem(`focuscorp_guest_capability:${new URL(backendUrl).origin}`);
  } catch { /* Optional browser persistence cannot block a new quiz. */ }
}
export function quizPlanHomePath(search: string) {
  const incoming = new URLSearchParams(search), query = new URLSearchParams();
  const code = incoming.get('qz');
  if (code === 'fc1' || code === 'tst1') query.set('qz', code);
  if (incoming.get('entry') === 'identity') query.set('entry', 'identity');
  if (incoming.get('flow') === 'account') query.set('flow', 'account');
  return `/${query.size ? `?${query}` : ''}`;
}
